<template>
	<div class="group-avatar" :style="containerStyle">
		<!-- 如果人数 <= 9，显示组合头像 -->
		<template v-if="memberCount > 0 && memberCount <= 9">
			<div v-for="(member, index) in displayMembers" :key="index" 
				class="avatar-item" :style="getAvatarItemStyle(index)">
				<head-image
					:name="member.nickName || member.showNickName"
					:url="getValidHeadImage(member.headImage)"
					:size="getAvatarItemSize(index)"
					:width="getAvatarItemWidth(index)"
					:height="getAvatarItemHeight(index)"
					radius="0"
					:isShowUserInfo="false">
				</head-image>
			</div>
		</template>
		<!-- 如果人数 > 9，显示默认头像 -->
		<template v-else>
			<head-image
				:name="defaultHeadName || groupName"
				:url="validDefaultHeadImage"
				:size="_size"
				radius="12px"
				:isShowUserInfo="false">
			</head-image>
		</template>
	</div>
</template>

<script>
import HeadImage from './HeadImage.vue';

export default {
	name: "group-avatar",
	components: {
		HeadImage
	},
	props: {
		groupId: {
			type: [Number, String],
			required: true
		},
		groupName: {
			type: String,
			default: ''
		},
		defaultHeadImage: {
			type: String,
			default: ''
		},
		defaultHeadName: {
			type: String,
			default: ''
		},
		size: {
			type: [Number, String],
			default: 42
		}
	},
	data() {
		return {
			groupMembers: [],
			colors: ["#1976d2", "#c7515a", "#e03697", "#85029b", "#c9b455", "#326eb6"]
		}
	},
	computed: {
		_size() {
			if (typeof this.size === 'number') {
				return this.size;
			} else if (typeof this.size === 'string') {
				return {
					'default': 42,
					'small': 38,
					'smaller': 32,
					'mini': 28,
					'minier': 24,
					'large': 50,
					'larger': 60,
				}[this.size] || 42;
			}
			return 42;
		},
		containerStyle() {
			return {
				width: this._size + 'px',
				height: this._size + 'px'
			};
		},
		avatarImageStyle() {
			return {
				width: this._size + 'px',
				height: this._size + 'px'
			};
		},
		avatarTextStyle() {
			return {
				width: this._size + 'px',
				height: this._size + 'px',
				backgroundColor: this.textColor,
				fontSize: (this._size * 0.5) + 'px'
			};
		},
		validDefaultHeadImage() {
			return this.getValidHeadImage(this.defaultHeadImage);
		},
		memberCount() {
			return this.groupMembers.filter(m => !m.quit).length;
		},
		displayMembers() {
			// 只显示前9个成员
			return this.groupMembers.filter(m => !m.quit).slice(0, 9);
		},
		textColor() {
			if (!this.groupName) return '#fff';
			let hash = 0;
			for (var i = 0; i < this.groupName.length; i++) {
				hash += this.groupName.charCodeAt(i);
			}
			return this.colors[hash % this.colors.length];
		}
	},
	methods: {
		getValidHeadImage(url) {
			return url || '';
		},
		loadGroupMembers() {
			if (!this.groupId) return;
			
			this.$http({
				url: `/group/members/${this.groupId}`,
				method: "GET"
			}).then((members) => {
				this.groupMembers = members || [];
			}).catch((err) => {
				console.error('加载群成员失败:', err);
				this.groupMembers = [];
			});
		},
		getAvatarItemStyle(index) {
			const count = this.memberCount;
			const size = this._size;
			const width = this.getAvatarItemWidth(index);
			const height = this.getAvatarItemHeight(index);
			let left = 0;
			let top = 0;

			if (count === 2) {
				left = index === 0 ? 0 : size / 2;
			} else if (count === 3) {
				if (index > 0) {
					left = size / 2;
					top = index === 1 ? 0 : size / 2;
				}
			} else if (count === 4) {
				left = (index % 2) * size / 2;
				top = Math.floor(index / 2) * size / 2;
			} else if (count <= 9) {
				left = (index % 3) * size / 3;
				top = Math.floor(index / 3) * size / 3;
			}

			return {
				width: width + 'px',
				height: height + 'px',
				position: 'absolute',
				left: left + 'px',
				top: top + 'px'
			};
		},
		getAvatarTextStyle(name, index) {
			if (!name) return this.avatarTextStyle;
			
			let hash = 0;
			for (var i = 0; i < name.length; i++) {
				hash += name.charCodeAt(i);
			}
			const color = this.colors[hash % this.colors.length];
			const itemSize = this.getAvatarItemSize(index);
			
			return {
				width: itemSize + 'px',
				height: itemSize + 'px',
				backgroundColor: color,
				fontSize: (itemSize * 0.5) + 'px'
			};
		},
		getAvatarItemSize(index) {
			return this.getAvatarItemWidth(index);
		},
		getAvatarItemWidth(index) {
			const count = this.memberCount;
			const size = this._size;

			if (count === 1) return size;
			if (count === 2 || count === 3 || count === 4) {
				// 相邻分块重叠 1px，避免浏览器缩放时出现白色十字缝。
				return size / 2 + (count === 3 && index > 0 ? 0 : 1);
			}
			return size / 3 + 1;
		},
		getAvatarItemHeight(index) {
			const count = this.memberCount;
			const size = this._size;

			if (count === 1 || count === 2 || (count === 3 && index === 0)) {
				return size;
			}
			if (count === 3 || count === 4) {
				return size / 2 + 1;
			}
			return size / 3 + 1;
		}
	},
	mounted() {
		this.loadGroupMembers();
	},
	watch: {
		groupId() {
			this.loadGroupMembers();
		}
	}
}
</script>

<style scoped lang="scss">
.group-avatar {
	position: relative;
	border-radius: 12px; /* 与单人头像 head-image 保持相同的矩形圆角 */
	overflow: hidden;
	background-color: #f0f0f0;
	
	.avatar-item {
		overflow: hidden;
		border: 0;
		margin: 0;
		padding: 0;
		font-size: 0;
		line-height: 0;

		> .head-image {
			display: block;
		}
		
		.avatar-image {
			width: 100%;
			height: 100%;
			border-radius: 0;
			object-fit: cover;
		}
		
		.avatar-text {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			color: white;
			font-weight: bold;
			border-radius: 0;
		}
	}
	
	.avatar-image {
		width: 100%;
		height: 100%;
		border-radius: 12px; /* 单头像模式：矩形圆角 */
		object-fit: cover;
	}
	
	.avatar-text {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-weight: bold;
		border-radius: 12px; /* 单头像模式：矩形圆角 */
	}
}
</style>
